import axios from 'axios';
import { useSetRecoilState } from 'recoil';
import useApiClient from '~/src/hooks/useApiClient';
import { detectOutliersState } from '~/src/ProjectResults/store';

import { snackbarMessageSelector } from '~/src/Snackbar/store';

const useDetectRegressionOutliers = () => {
  // Hooks
  const apiClient = useApiClient();
  const setMessage = useSetRecoilState(snackbarMessageSelector);

  // Recoil State
  const setDetectOutliersState = useSetRecoilState(detectOutliersState);

  const handleDetectOutliers = async (projectId: number, target: string) => {
    try {
      setDetectOutliersState((prev) => ({
        ...prev,
        isPending: true,
      }));
      await apiClient.put(
        `/automodeler/${projectId}/preprocess/detect-outliers`,
        {},
        { params: { target } },
      );
      // findOne(projectId);
    } catch {
      // error handled by finally block resetting state
    } finally {
      setDetectOutliersState((prev) => ({
        ...prev,
        isPending: false,
      }));
    }
  };

  const getOutliersFile = async (projectId: number) => {
    try {
      setDetectOutliersState((prev) => ({ ...prev, fetchFilePending: true }));
      const { data: url } = await apiClient.get(`/projects/${projectId}/urls`, {
        params: { file: 'outliers/outliers.json' },
      });
      const { data } = await axios.get(url);
      setDetectOutliersState((prev) => ({ ...prev, data }));
    } catch (err) {
      if (err.response?.status && err.response?.status !== 404) {
        setMessage({
          message: 'Failed to fetch outliers file',
          severity: 'error',
        });
      }
    } finally {
      setDetectOutliersState((prev) => ({ ...prev, fetchFilePending: false }));
    }
  };

  const saveOutliersFile = async (
    projectId: number,
    selectedOutliers: Array<string | number>,
  ) => {
    try {
      setDetectOutliersState((prev) => ({ ...prev, isPending: true }));
      // Get the current data from the state or fetch it if needed.
      // We assume data is already present since we are saving edits.
      const currentData = await new Promise((resolve) => {
        setDetectOutliersState((prev) => {
          resolve(prev.data);
          return prev;
        });
      });

      if (!currentData) return;

      const updatedData = {
        ...(currentData as any),
        flagged_indices: selectedOutliers,
      };

      // Get presigned URL from backend
      // Using pattern from useSaveOutliers.ts (using GET, not POST)
      const { data: url } = await apiClient.post(
        `/projects/${projectId}/urls/upload`,
        {},
        {
          params: { file: 'outliers/outliers.json' },
        },
      );

      // Convert JSON data to Blob
      const jsonString = JSON.stringify(updatedData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      // Upload to S3 using presigned URL
      await axios.put(url, blob, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Update local state with the saved data
      setDetectOutliersState((prev) => ({ ...prev, data: updatedData }));
      setMessage({
        message: 'Outliers saved successfully',
        severity: 'success',
      });
    } catch (err) {
      setMessage({
        message: 'Failed to save outliers file',
        severity: 'error',
      });
    } finally {
      setDetectOutliersState((prev) => ({ ...prev, isPending: false }));
    }
  };

  return { handleDetectOutliers, getOutliersFile, saveOutliersFile };
};

export default useDetectRegressionOutliers;
